import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import {
  getCustomerById,
  deleteCustomer,
  type Customer,
} from '../services/customer.service';
import { getCustomerCrops } from '../services/customerCrop.service';
import { getAllCropTypes } from '../services/cropType.service';
import { getAllRegions } from '../services/region.service';
import { getAllSeasonTimes } from '../services/seasonTime.service';
import { Timestamp } from 'firebase/firestore';
import { getRecentLogs, addLog, updateLog, deleteLog, type CustomerLog } from '../services/customerLog.service';
import { toastSuccess } from '../components/core';
import { usePermissions } from '../context/PermissionsContext';
import { Navbar } from '../components/Navbar';
import {
  BackButton,
  Button,
  CalendarIcon,
  ConfirmDialog,
  EmailIcon,
  Icon,
  LocationIcon,
  PencilIcon,
  PermissionGate,
  PhoneIcon,
  ReminderModal,
  Spinner,
  TrashIcon,
  type ReminderCrop,
  type ReminderCustomer,
} from '../components/core';
import { CustomerCropsSection, type ResolvedCrop } from '../components/CustomerCropsSection';
import { nextSeasonDate } from '../utils/nextSeasonDate';
import { ActivityLogSection } from '../components/ActivityLogSection';

function getInitials(firstName: string, lastName: string): string {
  return (firstName[0] + (lastName[0] ?? '')).toUpperCase();
}

export default function CustomerDetailPage() {
  const { user, signOut } = useAuth();
  const { org, orgId, orgSlug } = useOrg();
  const { hasPermission } = usePermissions();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [crops, setCrops] = useState<ResolvedCrop[]>([]);
  const [logs, setLogs] = useState<CustomerLog[]>([]);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderDescription, setReminderDescription] = useState('');

  useEffect(() => {
    if (!id || !orgId) return;

    const load = async () => {
      setLoading(true);
      try {
        // Customer is critical — any failure here blocks the whole page
        const customerData = await getCustomerById(orgId, id);

        if (!customerData || customerData.isDeleted) {
          setNotFound(true);
          return;
        }

        setCustomer(customerData);

        // Secondary data — failures show empty sections, not a broken page
        const [cropsResult, cropTypesResult, regionsResult, seasonTimesResult, logsResult] = await Promise.allSettled([
          getCustomerCrops(orgId, id),
          getAllCropTypes(orgId),
          getAllRegions(orgId),
          getAllSeasonTimes(orgId),
          getRecentLogs(orgId, id, 5),
        ]);

        if (
          cropsResult.status === 'fulfilled' &&
          cropTypesResult.status === 'fulfilled' &&
          regionsResult.status === 'fulfilled'
        ) {
          const cropTypeMap = new Map(cropTypesResult.value.map(ct => [ct.id, ct.name]));
          const regionMap = new Map(regionsResult.value.map(r => [r.id, r.name]));
          const seasonMap = seasonTimesResult.status === 'fulfilled'
            ? new Map(seasonTimesResult.value.map(s => [`${s.cropTypeId}_${s.regionId}`, s]))
            : new Map();
          setCrops(
            cropsResult.value
              .filter(c => !c.isDeleted)
              .map(c => {
                const season = seasonMap.get(`${c.cropTypeId}_${c.regionId}`);
                return {
                  id: `${c.cropTypeId}_${c.regionId}`,
                  cropTypeName: cropTypeMap.get(c.cropTypeId) ?? c.cropTypeId,
                  regionName: regionMap.get(c.regionId) ?? c.regionId,
                  seasonStart: season ? { month: season.startMonth, day: season.startDay } : undefined,
                  seasonEnd: season ? { month: season.endMonth, day: season.endDay } : undefined,
                };
              }),
          );
        }

        if (logsResult.status === 'fulfilled') {
          setLogs(logsResult.value);
        }
      } catch (err) {
        console.error('[CustomerDetailPage] load error:', err);
        setError(t('customers.loadError'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, orgId, t]);

  async function handleDelete() {
    if (!id || !orgId) return;
    setDeleting(true);
    try {
      await deleteCustomer(orgId, id);
      toastSuccess(t('toasts.customerDeleted'));
      navigate(`/org/${orgSlug}/customers`);
    } catch (err) {
      console.error('[CustomerDetailPage] delete error:', err);
      setError(t('delete.error'));
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  }

  async function handleAddNote(text: string) {
    if (!id || !orgId || !user) return;
    const newLog = await addLog(orgId, id, {
      message: text,
      createdBy: user.uid,
      userDisplayName: user.displayName ?? user.email ?? user.uid,
    });
    setLogs(prev => [newLog, ...prev].slice(0, 5));
  }

  async function handleEditNote(logId: string, newMessage: string) {
    if (!id || !orgId) return;
    try {
      await updateLog(orgId, id, logId, newMessage);
      setLogs(prev =>
        prev.map(log =>
          log.id === logId
            ? { ...log, message: newMessage, updatedAt: Timestamp.fromDate(new Date()) }
            : log,
        ),
      );
    } catch (error) {
      console.error('[CustomerDetailPage] edit note error:', error);
      throw error;
    }
  }

  async function handleDeleteNote(logId: string) {
    if (!id || !orgId) return;
    await deleteLog(orgId, id, logId);
    setLogs(prev => prev.filter(log => log.id !== logId));
  }

  function handleRemindFromLog(logMessage: string) {
    setReminderDescription(logMessage);
    setReminderOpen(true);
  }

  const [reminderInitialCropId, setReminderInitialCropId] = useState<string | undefined>(undefined);

  function handleRemindFromCrop(cropId: string) {
    setReminderDescription('');
    setReminderInitialCropId(cropId);
    setReminderOpen(true);
  }

  const reminderCrops: ReminderCrop[] = crops.map(c => ({
    id: c.id,
    cropTypeName: c.cropTypeName,
    regionName: c.regionName,
    seasonStartDate: c.seasonStart
      ? nextSeasonDate(c.seasonStart.month, c.seasonStart.day)
      : undefined,
  }));

  return (
    <div className="min-h-screen bg-green-50">
      <Navbar displayName={user?.displayName ?? null} onSignOut={signOut} onLogoClick={() => navigate('/')} onOrgNameClick={() => navigate(`/org/${orgSlug}`)} onAvatarClick={() => navigate('/profile')} orgName={org.name} />

      <main className="mx-auto max-w-2xl px-4 py-6 sm:px-6">
        <BackButton />

        {loading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <div className="rounded bg-red-50 px-4 py-3 font-body text-sm text-red-600">
            {error}
          </div>
        ) : notFound ? (
          <div className="py-16 text-center">
            <p className="font-display text-base font-semibold text-gray-700">{t('customers.notFound')}</p>
            <p className="mt-1 font-body text-sm text-gray-400">{t('customers.notFoundDetail')}</p>
          </div>
        ) : customer ? (
          <>
            {/* Hero banner — name, meta, and actions in one card */}
            <div className="mb-6 rounded-[14px] bg-gradient-to-br from-green-800 to-green-700 p-[18px] text-white shadow-md sm:p-6">
              {/*
                Mobile:  3 rows — [avatar + name], [meta], [buttons]
                Desktop: 2 cols — [avatar + name + meta] | [buttons bottom]
              */}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4">

                {/* Col 1: avatar + name + badge (+ meta on desktop) */}
                <div className="flex items-center gap-3.5 sm:items-start">
                  <div className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full bg-white/20 font-display text-xl font-semibold sm:h-16 sm:w-16 sm:text-2xl">
                    {getInitials(customer.firstName, customer.lastName)}
                  </div>
                  <div>
                    <h1 className="font-display text-[1.15rem] font-semibold leading-tight sm:text-2xl">
                      {customer.firstName} {customer.lastName}
                    </h1>
                    <span className="mt-1.5 inline-block rounded-full bg-white/[.18] px-2.5 py-0.5 font-body text-xs font-medium">
                      {t('customers.farmerBadge')}
                    </span>
                    {/* Meta — desktop only, inline under name */}
                    <div className="mt-3 hidden sm:flex sm:flex-col sm:gap-1.5">
                      <span className="flex items-center gap-1.5 font-body text-sm text-white/80">
                        <Icon size="sm"><PhoneIcon /></Icon>
                        <a href={`tel:${customer.phone}`} className="hover:text-white">{customer.phone}</a>
                      </span>
                      {customer.address && (
                        <span className="flex items-center gap-1.5 font-body text-sm text-white/80">
                          <Icon size="sm"><LocationIcon /></Icon>
                          {customer.address}
                        </span>
                      )}
                      <span className="flex items-center gap-1.5 font-body text-sm text-white/80">
                        <Icon size="sm"><EmailIcon /></Icon>
                        {customer.email
                          ? <a href={`mailto:${customer.email}`} className="hover:text-white">{customer.email}</a>
                          : <span className="italic opacity-60">{t('customers.noEmail')}</span>
                        }
                      </span>
                      {customer.registrationDate && (
                        <span className="flex items-center gap-1.5 font-body text-sm text-white/80">
                          <Icon size="sm"><CalendarIcon /></Icon>
                          {t('customers.since')} {new Intl.DateTimeFormat(i18n.language).format(new Date(customer.registrationDate + 'T12:00:00'))}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Meta — mobile only, own row left-aligned */}
                <div className="flex flex-col gap-1 sm:hidden">
                  <span className="flex items-center gap-1.5 font-body text-sm text-white/80">
                    <Icon size="sm"><PhoneIcon /></Icon>
                    <a href={`tel:${customer.phone}`} className="hover:text-white">{customer.phone}</a>
                  </span>
                  {customer.address && (
                    <span className="flex items-center gap-1.5 font-body text-sm text-white/80">
                      <Icon size="sm"><LocationIcon /></Icon>
                      {customer.address}
                    </span>
                  )}
                  <span className="flex items-center gap-1.5 font-body text-sm text-white/80">
                    <Icon size="sm"><EmailIcon /></Icon>
                    {customer.email
                      ? <a href={`mailto:${customer.email}`} className="hover:text-white">{customer.email}</a>
                      : <span className="italic opacity-60">{t('customers.noEmail')}</span>
                    }
                  </span>
                  {customer.registrationDate && (
                    <span className="flex items-center gap-1.5 font-body text-sm text-white/80">
                      <Icon size="sm"><CalendarIcon /></Icon>
                      {t('customers.since')} {new Intl.DateTimeFormat(i18n.language).format(new Date(customer.registrationDate + 'T12:00:00'))}
                    </span>
                  )}
                </div>

                {/* Actions — full-width on mobile, auto-width on desktop */}
                <div className="flex gap-2 sm:shrink-0">
                  <Button
                    variant="heroGhost"
                    size="sm"
                    className="flex-1 justify-center rounded-lg sm:flex-none"
                    onClick={() => navigate(`/org/${orgSlug}/customers/${id}/edit`)}
                  >
                    <Icon size="sm"><PencilIcon /></Icon>
                    {t('customers.edit')}
                  </Button>
                  <PermissionGate permission="customers.delete">
                    <Button
                      variant="heroDanger"
                      size="sm"
                      className="flex-1 justify-center rounded-lg sm:flex-none"
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      <Icon size="sm"><TrashIcon /></Icon>
                      {t('customers.delete')}
                    </Button>
                  </PermissionGate>
                </div>

              </div>
            </div>

            <CustomerCropsSection crops={crops} onRemind={handleRemindFromCrop} />

            <ActivityLogSection
              logs={logs}
              currentUserId={user?.uid}
              canDeleteAny={hasPermission('customers.delete')}
              onAddNote={handleAddNote}
              onEditNote={handleEditNote}
              onDeleteNote={handleDeleteNote}
              onRemind={handleRemindFromLog}
              onViewAll={() => navigate(`/org/${orgSlug}/customers/${id}/logs`)}
            />

            <ReminderModal
              isOpen={reminderOpen}
              onClose={() => { setReminderOpen(false); setReminderInitialCropId(undefined); }}
              crops={reminderCrops}
              initialCropId={reminderInitialCropId}
              initialDescription={reminderDescription}
              customerInfo={customer ? {
                fullName: `${customer.firstName} ${customer.lastName}`,
                phone: customer.phone,
                address: customer.address,
              } satisfies ReminderCustomer : undefined}
            />

            <ConfirmDialog
              isOpen={showDeleteConfirm}
              title={t('delete.title')}
              message={
                <>{t('delete.messagePre')} <strong>{customer.firstName} {customer.lastName}</strong>{t('delete.messagePost')}</>
              }
              confirmLabel={t('delete.confirm')}
              loadingLabel={t('delete.deleting')}
              loading={deleting}
              onConfirm={handleDelete}
              onCancel={() => setShowDeleteConfirm(false)}
            />
          </>
        ) : null}
      </main>
    </div>
  );
}
